import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	FileText,
	Search,
	DollarSign,
	CheckCircle,
	XCircle,
	Clock,
	ChevronLeft,
	ChevronRight,
	ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

const statusColors: Record<string, string> = {
	paid: "bg-green-100 text-green-700",
	unpaid: "bg-red-100 text-red-700",
	open: "bg-blue-100 text-blue-700",
	voided: "bg-gray-100 text-gray-700",
	draft: "bg-yellow-100 text-yellow-700",
};

const invoiceStatuses = ["paid", "unpaid", "open", "voided", "draft"];

interface InvoiceStats {
	totalInvoices: number;
	paidInvoices: number;
	failedInvoices: number;
	openInvoices: number;
	totalRevenue: number;
}

interface Invoice {
	_id: string;
	stripeInvoiceId?: string;
	organizationId?: {
		_id: string;
		name: string;
	};
	amount: number;
	currency: string;
	status: string;
	paymentDate?: string;
	periodStart?: string;
	periodEnd?: string;
	invoicePdfUrl?: string;
	createdAt: string;
}

async function fetchInvoices(params: {
	page: number;
	limit: number;
	status?: string;
}) {
	const token = getAccessToken();
	const queryParams = new URLSearchParams();
	queryParams.append("page", String(params.page));
	queryParams.append("limit", String(params.limit));
	if (params.status) queryParams.append("status", params.status);

	const res = await fetch(adminApiUrl(`/invoices?${queryParams.toString()}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchInvoiceStats(): Promise<InvoiceStats> {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/invoices/stats"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

function formatCurrency(amount: number, currency: string) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency?.toUpperCase() || "USD",
	}).format(amount / 100);
}

export function InvoiceList() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");

	const { data: stats } = useQuery({
		queryKey: ["admin-invoice-stats"],
		queryFn: fetchInvoiceStats,
	});

	const { data, isLoading, error } = useQuery({
		queryKey: ["admin-invoices", page, selectedStatus],
		queryFn: () =>
			fetchInvoices({
				page,
				limit: 20,
				status: selectedStatus || undefined,
			}),
	});

	const invoices: Invoice[] = data?.invoices || [];
	const pagination = {
		total: data?.total || 0,
		page: data?.page || 1,
		limit: data?.limit || 20,
		pages: data?.totalPages || 1,
	};

	// Client-side search by org name
	const filteredInvoices = search
		? invoices.filter((inv) =>
				inv.organizationId?.name
					?.toLowerCase()
					.includes(search.toLowerCase()),
			)
		: invoices;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
				<p className="text-gray-500 mt-1">
					View all invoices and payment history
				</p>
			</div>

			{/* Stats Cards */}
			{stats && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-100 rounded-lg">
									<DollarSign className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{formatCurrency(stats.totalRevenue || 0, "USD")}
									</p>
									<p className="text-sm text-gray-500">Total Revenue</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<CheckCircle className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.paidInvoices}</p>
									<p className="text-sm text-gray-500">Paid</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-red-100 rounded-lg">
									<XCircle className="w-5 h-5 text-red-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.failedInvoices}</p>
									<p className="text-sm text-gray-500">Failed</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-yellow-100 rounded-lg">
									<Clock className="w-5 h-5 text-yellow-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.openInvoices}</p>
									<p className="text-sm text-gray-500">Open</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
							<Input
								placeholder="Search by organization name..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-10"
							/>
						</div>
						<select
							value={selectedStatus}
							onChange={(e) => {
								setSelectedStatus(e.target.value);
								setPage(1);
							}}
							className="px-3 py-2 border rounded-md text-sm"
						>
							<option value="">All Statuses</option>
							{invoiceStatuses.map((status) => (
								<option key={status} value={status}>
									{status.charAt(0).toUpperCase() + status.slice(1)}
								</option>
							))}
						</select>
					</div>
				</CardContent>
			</Card>

			{/* Invoice Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Invoices</span>
						<span className="text-sm font-normal text-gray-500">
							{pagination.total} total
						</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : error ? (
						<div className="text-center py-12 text-red-500">
							Failed to load invoices
						</div>
					) : filteredInvoices.length === 0 ? (
						<div className="text-center py-12 text-gray-500">
							No invoices found
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b">
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Organization
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Amount
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Status
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Payment Date
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Period
											</th>
											<th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">
												Invoice
											</th>
										</tr>
									</thead>
									<tbody>
										{filteredInvoices.map((invoice) => (
											<tr
												key={invoice._id}
												className="border-b last:border-0 hover:bg-gray-50"
											>
												<td className="py-3 px-4">
													<div className="flex items-center gap-3">
														<div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
															<FileText className="w-4 h-4 text-gray-500" />
														</div>
														<div>
															<p className="font-medium text-gray-900">
																{invoice.organizationId?.name || "Unknown"}
															</p>
															{invoice.stripeInvoiceId && (
																<p className="text-xs text-gray-400">
																	{invoice.stripeInvoiceId}
																</p>
															)}
														</div>
													</div>
												</td>
												<td className="py-3 px-4">
													<span className="font-medium">
														{formatCurrency(
															invoice.amount,
															invoice.currency,
														)}
													</span>
												</td>
												<td className="py-3 px-4">
													<Badge
														className={
															statusColors[invoice.status] || "bg-gray-100"
														}
													>
														{invoice.status?.charAt(0).toUpperCase() +
															invoice.status?.slice(1)}
													</Badge>
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{invoice.paymentDate
														? new Date(
																invoice.paymentDate,
															).toLocaleDateString()
														: "-"}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{invoice.periodStart && invoice.periodEnd
														? `${new Date(invoice.periodStart).toLocaleDateString()} - ${new Date(invoice.periodEnd).toLocaleDateString()}`
														: "-"}
												</td>
												<td className="py-3 px-4 text-right">
													{invoice.invoicePdfUrl ? (
														<a
															href={invoice.invoicePdfUrl}
															target="_blank"
															rel="noopener noreferrer"
														>
															<Button variant="ghost" size="sm">
																<ExternalLink className="w-4 h-4 mr-1" />
																PDF
															</Button>
														</a>
													) : (
														<span className="text-sm text-gray-400">-</span>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {pagination.page} of {pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={page <= 1}
											onClick={() => setPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={page >= pagination.pages}
											onClick={() => setPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
